import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "agentFees", "total", "difference", "submit", "warnings"]
  static values = { received: String }

  connect() {
    this.nextDeduction = Date.now()
    this.refresh()
    // Keep the existing modal behaviour, with enough space for several cards.
    this.element.closest(".modal-dialog")?.classList.add("modal-lg")
  }

  decimal(value) {
    if (!/^\d+(\.\d{1,2})?$/.test(value)) return null
    const [whole, fraction = ""] = value.split(".")
    return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"))
  }

  money(value) {
    const absolute = value < 0n ? -value : value
    return `${value < 0n ? "-" : ""}£${(absolute / 100n).toLocaleString("en-GB")}.${String(absolute % 100n).padStart(2, "0")}`
  }

  changed(event) {
    if (event?.target.closest('[data-rent-receipt-target="warnings"]')) return false
    if (event) this.warningsTarget.replaceChildren()
    return true
  }

  refresh(event) {
    if (!this.changed(event)) return
    let agentFees = 0n
    let feesComplete = true
    let total = 0n
    let complete = true
    let included = false
    this.rowTargets.forEach(row => {
      const selected = row.querySelector("[data-included]").checked
      row.querySelector("[data-row-fields]").hidden = !selected
      if (!selected) return
      included = true
      const gross = this.decimal(row.querySelector("[data-gross]").value)
      const rate = this.decimal(row.dataset.rate)
      const calculated = gross !== null && rate !== null ? (gross * rate + 5000n) / 10000n : null
      row.querySelector("[data-calculated-fee]").textContent = calculated === null ? "Not available" : this.money(calculated)
      const override = row.querySelector("[data-fee-override]").value
      const fee = override ? this.decimal(override) : calculated
      if (fee === null) feesComplete = false
      else agentFees += fee
      let deductions = 0n
      let valid = gross !== null && fee !== null
      row.querySelectorAll("[data-deduction]").forEach(deduction => {
        const amount = this.decimal(deduction.querySelector("[data-deduction-amount]").value)
        if (amount === null || amount <= 0n) valid = false
        else deductions += amount
        if (!deduction.querySelector("select").value) valid = false
      })
      if (valid) {
        const net = gross - fee - deductions
        row.querySelector("[data-row-net]").textContent = this.money(net)
        total += net
      } else {
        row.querySelector("[data-row-net]").textContent = "Incomplete"
        complete = false
      }
    })
    const received = this.decimal(this.receivedValue)
    const validReceipt = received !== null && received > 0n
    const difference = validReceipt ? total - received : null
    this.agentFeesTarget.textContent = feesComplete ? this.money(agentFees) : "Incomplete"
    this.totalTarget.textContent = complete ? this.money(total) : "Incomplete"
    this.differenceTarget.textContent = !validReceipt ? "This review needs an incoming receipt. Reopen categorisation to check it." : !complete ? "Complete the breakdown to check the difference." : difference === 0n
      ? "The breakdown agrees with the amount received."
      : `Received is ${this.money(difference < 0n ? -difference : difference)} ${difference > 0n ? "below" : "above"} the breakdown.`
    this.submitTarget.disabled = !validReceipt || !complete || !included || difference !== 0n
  }

  resetFee(event) {
    event.target.closest('[data-rent-receipt-target="row"]').querySelector("[data-fee-override]").value = ""
    this.refresh(event)
  }

  addDeduction(event) {
    const row = event.target.closest('[data-rent-receipt-target="row"]')
    const markup = row.querySelector("template").innerHTML.replaceAll("NEW_DEDUCTION", String(++this.nextDeduction))
    row.querySelector("[data-deductions]").insertAdjacentHTML("beforeend", markup)
    this.refresh(event)
  }

  removeDeduction(event) {
    event.target.closest("[data-deduction]").remove()
    this.refresh(event)
  }
}
